import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation, Trans } from 'react-i18next';
import { components } from 'react-select';
import Footer from '@components/layouts/Truva/Layout/Footer';
import Navigation from '@components/layouts/Truva/Layout/Navigation';
import { parse } from 'qs';
import { NavLink, useLocation } from 'react-router-dom';
import routes from '@root/routes';
import useMarket from '@root/shared/useMarket';
import OnlyInDesktopTablet from '@components/shared/OnlyInDesktopTablet';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CustomIcon, OnlyInDesktop, OnlyInTablet } from '@components/shared';
import { ButtonLink } from '@components/shared/V2Button';
import OnlyInMobile from '@components/shared/OnlyInMobile';
import useStickyState from '@shared/useStickyState';
import useAssetPath from '@shared/useAssetPath';
import COLORS from '@shared/colorConstants';
import actions from '@store/actions/homepage/analyticsActions';
import { CSSTransition } from 'react-transition-group';
import { StoreState } from '@root/types';
import { getCurrentUser, hasCurrentUserLoaded } from '@root/store/selectors';
import HomepageSearch from './HomepageSearch';
import s from './Homepage.module.less';
import Triptych from './Triptych';
import ExclusiveListingsSection from './ExclusiveListingsSection';
import MarketToggleSelect from '../ListingSearch/MarketSelector/MarketToggleSelect';
import SiteVisitorModal from './SiteVisitorModal';

const getMarketByValue = (options, marketValue) =>
  options
    .map(option => option.options)
    .flat()
    .find(option => option.value === marketValue);

const SingleValue = props => {
  return (
    <components.SingleValue {...props}>
      <span className={s.valueContainer}>
        {props.children}’s <CustomIcon className={s.dropdownIndicator} type="chevron-down" />
      </span>
    </components.SingleValue>
  );
};

export const RawHomepage = () => {
  const { t } = useTranslation('homepage');
  const assetPath = useAssetPath();
  const dispatch = useDispatch();
  const flags = useFlags();
  const { options } = useMarket();
  const homeRedesignFlag = flags['homepage-redesign'];
  // would normally use useParams, but it doesnt
  // seem to work w/ a root route
  const { search } = useLocation();
  const { market: queryParamMarket } = parse(search, { ignoreQueryPrefix: true });
  const isCurrentUserLoaded = useSelector((state: StoreState) => hasCurrentUserLoaded(state));
  const currentUser = useSelector((state: StoreState) => getCurrentUser(state));
  const [siteVisitorMarket, setSiteVisitorMarket] = useStickyState<string>('', 'siteVisitorMarket');
  const showSiteVisitorModal =
    isCurrentUserLoaded && !currentUser && !siteVisitorMarket && !queryParamMarket;

  const [selectedMarket, setSelectedMarket] = useState(
    getMarketByValue(
      options,
      queryParamMarket || currentUser?.marketSlug || siteVisitorMarket || 'new_york_city',
    ),
  );

  useEffect(() => {
    dispatch(actions.homepageLoaded());
  }, []);

  useEffect(() => {
    if (currentUser) {
      setSelectedMarket(getMarketByValue(options, currentUser.marketSlug));
    }
  }, [currentUser]);

  useEffect(() => {
    if (siteVisitorMarket && !currentUser && !queryParamMarket) {
      setSelectedMarket(getMarketByValue(options, siteVisitorMarket));
    }
  }, [currentUser, siteVisitorMarket, queryParamMarket]);

  const toggleMarket = market => {
    setSelectedMarket(getMarketByValue(options, market));
  };

  const handleClickValuePropAndFeatureTriptych = url =>
    dispatch(actions.valuePropAndFeatureTriptychClicked({ url }));

  return (
    <>
      {flags['search-redesign'] ? <Navigation /> : null}
      <div>
        <div className={homeRedesignFlag ? s.searchContainerRedesign : s.searchContainer}>
          {homeRedesignFlag ? (
            <>
              {isCurrentUserLoaded && (
                <>
                  {showSiteVisitorModal ? (
                    <SiteVisitorModal
                      chooseMarket={market => {
                        if (!market) return;
                        setSiteVisitorMarket(market);
                      }}
                    />
                  ) : (
                    <CSSTransition
                      in
                      appear
                      timeout={
                        /* this is because of the site visitor modal, 
                        you want it to animate in immediately */
                        currentUser ? 1000 : 0
                      }
                      classNames="lines all"
                    >
                      <div className={s.searchContentContainer}>
                        <div>{t('headerText.firstLine')}</div>
                        <div>
                          <MarketToggleSelect
                            name="homepageMarketSelector"
                            options={options}
                            value={selectedMarket}
                            onChange={toggleMarket}
                            menuPlacement="bottom"
                            overrideComponents={{
                              SingleValue,
                            }}
                            overrideCustomStyles={{
                              singleValue: base => ({
                                ...base,
                                color: COLORS.white,
                                marginLeft: '0',
                                marginRight: '0',
                                paddingRight: '8px',
                                position: 'static',
                                transform: 'none',
                                textOverflow: '',
                                borderBottom: `1px dashed ${COLORS.white}`,
                              }),
                              dropdownIndicator: base => ({
                                ...base,
                                display: 'none',
                              }),
                              valueContainer: base => ({
                                ...base,
                                padding: '0',
                              }),
                              container: base => ({
                                ...base,
                              }),
                            }}
                          />
                        </div>

                        <div>{t('headerText.thirdLine')}</div>

                        <div>{t('headerText.fourthLine')}</div>
                        <div className={s.searchButton}>
                          <NavLink to={routes.search(selectedMarket?.value as string)}>
                            <OnlyInDesktop>
                              <Button type="primary" size="large" icon="magnifying-glass">
                                {t('buttonText')}
                              </Button>
                            </OnlyInDesktop>
                            <OnlyInTablet>
                              <Button type="primary" size="medium" icon="magnifying-glass">
                                {t('buttonText')}
                              </Button>
                            </OnlyInTablet>
                            <OnlyInMobile>
                              <Button type="primary" size="small" icon="magnifying-glass">
                                {t('buttonText')}
                              </Button>
                            </OnlyInMobile>
                          </NavLink>
                        </div>
                      </div>
                    </CSSTransition>
                  )}
                </>
              )}
              <div className={s.searchContainerEffect} />
            </>
          ) : (
            <>
              <div className={s.heroTextLineOne}>
                <OnlyInDesktopTablet>{t('heroText.firstLine')}</OnlyInDesktopTablet>
                <OnlyInMobile>{t('heroText.firstLineMobile')}</OnlyInMobile>
              </div>
              <div className={s.heroTextLineTwo}>
                <OnlyInDesktopTablet>{t('heroText.secondLine')}</OnlyInDesktopTablet>
                <OnlyInMobile>{t('heroText.secondLineMobile')}</OnlyInMobile>
              </div>
              <HomepageSearch />
            </>
          )}
        </div>
        <ExclusiveListingsSection selectedMarket={selectedMarket} />
        <div className={s.valuePropsAndFeatures}>
          <div className={s.valuePropsAndFeaturesResponsiveWidth}>
            <Triptych>
              <div className={s.valuePropOrFeatureCard}>
                <div className={s.valuePropOrFeatureCardContent}>
                  <img
                    src={assetPath('/images/FirstToMarketListing.png')}
                    alt="First to Market listings"
                  />
                  <h2>{t('featuresAndValueProps.firstToMarketListings.title')}</h2>
                  <p>
                    <Trans
                      ns="homepage"
                      i18nKey="featuresAndValueProps.firstToMarketListings.description"
                      components={{ noWrap: <span className={s.noWrap} /> }}
                    />
                  </p>
                </div>
                <ButtonLink
                  type="secondary"
                  size="large"
                  href="http://www.vts.com/first-to-market-listings"
                  openInNewTab={false}
                  className={s.ctaButton}
                  onClick={handleClickValuePropAndFeatureTriptych}
                >
                  {t('featuresAndValueProps.cta')}
                </ButtonLink>
              </div>
              <div className={s.valuePropOrFeatureCard}>
                <div className={s.valuePropOrFeatureCardContent}>
                  <img src={assetPath('/images/Canvassing.png')} alt="Virtual Tourbook" />
                  <h2>{t('featuresAndValueProps.virtualTourbook.title')}</h2>
                  <p>{t('featuresAndValueProps.virtualTourbook.description')}</p>
                </div>
                <ButtonLink
                  type="secondary"
                  size="large"
                  href="http://www.vts.com/canvassing"
                  openInNewTab={false}
                  className={s.ctaButton}
                  onClick={handleClickValuePropAndFeatureTriptych}
                >
                  {t('featuresAndValueProps.cta')}
                </ButtonLink>
              </div>
              <div className={s.valuePropOrFeatureCard}>
                <div className={s.valuePropOrFeatureCardContent}>
                  <img src={assetPath('/images/Sublease.png')} alt="Sublease" />
                  <h2>{t('featuresAndValueProps.subleases.title')}</h2>
                  <p>{t('featuresAndValueProps.subleases.description')}</p>
                </div>
                <ButtonLink
                  type="secondary"
                  size="large"
                  href="http://www.vts.com/subleases"
                  openInNewTab={false}
                  className={s.ctaButton}
                  onClick={handleClickValuePropAndFeatureTriptych}
                >
                  {t('featuresAndValueProps.cta')}
                </ButtonLink>
              </div>
            </Triptych>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default RawHomepage;
